/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import { Box, Typography, Button, CircularProgress, Dialog, DialogContent } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { PerformanceReportResponse } from "../../lib/Types";
import PerformanceReportTables from "./PerformanceReportTables";
import dayjs from "dayjs";

interface ViewReportModalProps {
  open: boolean;
  report: PerformanceReportResponse | null;
  isLoading?: boolean;
  onDiscard: () => void;
  onSaveReport: () => void;
  onViewDetails: () => void;
  onDownloadData: () => void;
  isDiscarding?: boolean;
}

const ViewReportModal: React.FC<ViewReportModalProps> = ({
  open,
  report,
  isLoading = false,
  onDiscard,
  onSaveReport,
  onViewDetails,
  onDownloadData,
  isDiscarding = false,
}) => {
  const startDate = report?.billing_cycle_start
    ? dayjs(report.billing_cycle_start).format("MMM D")
    : "";
  const endDate = report?.billing_cycle_end
    ? dayjs(report.billing_cycle_end).format("MMM D YYYY")
    : "";

  const actionsDisabled = isLoading || isDiscarding;

  return (
    <Dialog
      open={open}
      onClose={actionsDisabled ? undefined : onDiscard}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          p: 4,
          borderRadius: "4px",
          boxShadow: "0px 4px 18px 0px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* Header: Title + Action Buttons */}
          <Box>
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  fontSize: "24px",
                  lineHeight: 1.334,
                  color: "text.primary",
                }}
              >
                View Bill Savings
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onDiscard}
                  disabled={actionsDisabled}
                >
                  Close &amp; Discard
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSaveReport}
                  disabled={actionsDisabled}
                >
                  Save Report...
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                gap: 2,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">
                Generating report… This may take a few seconds.
              </Typography>
            </Box>
          )}

          {/* Report Content (shown once report is ready) */}
          {!isLoading && report && (
            <Box>
              {/* Date Range */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: 1.75,
                    letterSpacing: "0.15px",
                    color: "text.secondary",
                  }}
                >
                  {startDate} - {endDate}
                </Typography>
              </Box>

              {/* View Details + Download Buttons */}
              <Box sx={{ display: "flex", gap: 1, mb: 6 }}>
                <Button variant="outlined" color="secondary" size="small" onClick={onViewDetails}>
                  View Details
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={onDownloadData}
                >
                  Download Data
                </Button>
              </Box>

              {/* Report Tables */}
              <PerformanceReportTables report={report} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewReportModal;
